import { useState } from "react";
import { Check, ChevronDown, History, Info, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { DateQuickPicks } from "@/components/ui/date-quick-picks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { useEditIssue } from "@/hooks/use-issue-mutations";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProjectPeople } from "@/hooks/use-project-people";
import { ApiError } from "@/lib/api";
import { formatDueDate, personInitials, personName } from "@/lib/issues";
import { cn } from "@/lib/utils";
import type { ProjectIssue } from "@/types/api";

interface IssueEditSheetProps {
  projectId: string;
  issue: ProjectIssue;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * `dialog` centres the same form in a popup — desktop, where a sheet across
   * a wide monitor lands far from the panel that opened it, and where the
   * update form is a dialog too. Defaults to the bottom sheet. See
   * [[issue-edit-sheet.spec]].
   */
  presentation?: "drawer" | "dialog";
}

/**
 * Amend the four facts an issue's header states: title, assignee, due date and
 * description.
 *
 * One sheet, one save, one trail entry. Not inline tap-to-edit — on a phone,
 * four separate commits to four separate fields is four chances to lose a
 * half-made change, and it would put four entries in the trail for what was one
 * decision. See [[issue-edit-sheet.spec]].
 *
 * Status is deliberately absent. The only thing that moves it is posting an
 * update, which is the governing idea of the whole feature ([[issues.spec]]).
 */
export function IssueEditSheet({
  projectId,
  issue,
  open,
  onOpenChange,
  presentation = "drawer",
}: IssueEditSheetProps) {
  /* Keyed on open so each edit starts from the issue as it now stands, and a
     set of changes abandoned by tapping Cancel cannot be committed by accident
     the next time the sheet opens — see [[issue-update-sheet.spec]] for why
     this is a remount, not an effect. */
  const form = (
    <EditForm
      key={open ? "open" : "closed"}
      projectId={projectId}
      issue={issue}
      presentation={presentation}
      onClose={() => onOpenChange(false)}
    />
  );

  if (presentation === "dialog") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* `bare`: the form lays out its own header, scrolling body and
            action bar, exactly as it does inside the drawer. */}
        <DialogContent
          bare
          aria-describedby={undefined}
          className="sm:max-w-md"
        >
          {form}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh]">{form}</DrawerContent>
    </Drawer>
  );
}

interface EditFormProps {
  projectId: string;
  issue: ProjectIssue;
  presentation: "drawer" | "dialog";
  onClose: () => void;
}

/**
 * The server's own sentence about a rejected field, when it sent one.
 *
 * A rejected assignee is the case this exists for: "that person is no longer on
 * this project" is a real answer, and paraphrasing it as a connection failure
 * would send someone hunting a bug that is not there. Anything that is not a
 * short plain string is ignored, so a stack trace or a DRF `detail` object
 * cannot leak into the sheet.
 */
function fieldErrorMessage(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  for (const value of Object.values(body as Record<string, unknown>)) {
    const message = Array.isArray(value) ? value[0] : value;
    if (typeof message === "string" && message && message.length <= 200) {
      return message;
    }
  }
  return null;
}

function EditForm({ projectId, issue, presentation, onClose }: EditFormProps) {
  // The title must belong to whichever primitive is hosting the form — each
  // one wires its own title to its own accessible name.
  const Header = presentation === "dialog" ? DialogHeader : DrawerHeader;
  const Title = presentation === "dialog" ? DialogTitle : DrawerTitle;

  const [title, setTitle] = useState(issue.title);
  const [description, setDescription] = useState(issue.description ?? "");
  const [assignedTo, setAssignedTo] = useState<string | null>(
    issue.assigned_to?.id ?? null,
  );
  const [dueDate, setDueDate] = useState<string | null>(issue.due_date);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // The issue as it stood the moment the sheet opened — snapshotted once, from
  // the same instant the fields above took their values, and deliberately never
  // re-read from the live prop.
  //
  // The detail query refetches on window focus and on any issue mutation
  // anywhere, so a colleague's edit — or a phone coming back from the camera —
  // hands this component a different `issue` mid-edit while it stays mounted.
  // A baseline read from that would mark a field nobody touched as changed, put
  // a "Was …" hint under it, and let Save send a value the user never chose,
  // reverting the other person's edit and stamping it in the trail as this
  // user's own decision. What changed can only mean what changed since editing
  // began.
  const [original] = useState(() => ({
    title: issue.title.trim(),
    description: (issue.description ?? "").trim(),
    assignee: issue.assigned_to,
    assignedTo: issue.assigned_to?.id ?? null,
    dueDate: issue.due_date,
  }));

  const { people } = useProjectPeople(projectId);
  const { data: currentUser } = useCurrentUser();
  const editIssue = useEditIssue();

  const selectedPerson =
    people.find((person) => person.id === assignedTo) ??
    // Falls back to the assignee the sheet opened on: the project's person
    // list can lag or page, and a picker that read "Nobody yet" for an issue
    // that plainly has an owner would invite someone to "fix" it.
    (original.assignee?.id === assignedTo ? original.assignee : null);

  // Compared against the snapshot, not against what the field started as, so
  // typing a change and undoing it disables Save again. Titles and descriptions
  // are compared trimmed, and sent trimmed, so a stray space cannot put a no-op
  // entry in the trail.
  const titleChanged = title.trim() !== original.title;
  const descriptionChanged = description.trim() !== original.description;
  const assigneeChanged = assignedTo !== original.assignedTo;
  const dueDateChanged = (dueDate ?? null) !== original.dueDate;

  const dirty =
    titleChanged || descriptionChanged || assigneeChanged || dueDateChanged;

  const submit = () => {
    if (!title.trim()) {
      setErrorMessage("Give the issue a title.");
      return;
    }
    setErrorMessage(null);
    editIssue.mutate(
      {
        projectId,
        issueId: issue.id,
        // Only what changed. An absent field is not considered by the server,
        // which is what stops an edit of the due date from also re-writing a
        // description the user never touched — and what makes a retry after a
        // lost response harmless, since the second attempt finds every value
        // already equal and writes no second trail entry.
        ...(titleChanged ? { title: title.trim() } : {}),
        ...(descriptionChanged ? { description: description.trim() } : {}),
        ...(assigneeChanged ? { assignedToId: assignedTo } : {}),
        ...(dueDateChanged ? { dueDate } : {}),
      },
      {
        onSuccess: onClose,
        onError: (error) => {
          const apiStatus = error instanceof ApiError ? error.status : null;
          // A closed issue refuses the edit. The pencil is hidden on a closed
          // issue, so this only happens when it closed under an open sheet —
          // which makes it worth saying precisely, because the fix is a reopen
          // rather than a retry.
          if (apiStatus === 409) {
            setErrorMessage("This issue is closed. Reopen it before editing.");
            return;
          }
          // The issue is gone. Retrying will never succeed, and "check your
          // connection" would have someone on a jobsite hunting signal for a
          // save that has nowhere left to land.
          //
          // What is deliberately NOT said is why. A deleted record is the
          // documented cause, but this route is nested under the project, so a
          // stale project id 404s from the project lookup before this issue is
          // ever considered. An error may say what happened and what to do
          // about it; it must not assert a cause the response does not carry.
          // A confidently wrong explanation costs more than a vague correct
          // one, because it sends someone after the wrong problem.
          if (apiStatus === 404) {
            setErrorMessage("This issue no longer exists.");
            return;
          }
          if (apiStatus === 403) {
            setErrorMessage("You don't have permission to edit this issue.");
            return;
          }
          if (apiStatus === 400) {
            setErrorMessage(
              fieldErrorMessage(error instanceof ApiError ? error.body : null) ??
                "Those changes were refused. Check the fields and try again.",
            );
            return;
          }
          setErrorMessage(
            "Couldn't save those changes. Check your connection and try again.",
          );
        },
      },
    );
  };

  return (
    <>
      <Header
        className={cn(presentation === "dialog" && "p-4 pr-12 text-left")}
      >
        <Title>Edit issue</Title>
      </Header>

      <div className="flex flex-col gap-5 overflow-y-auto px-4 pb-4">
        <section className="flex flex-col gap-2">
          <label htmlFor="issue-edit-title" className="text-[13px] font-medium">
            Title
          </label>
          <Input
            id="issue-edit-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="What needs fixing?"
            className={cn(titleChanged && "border-tertiary")}
          />
          <WasValue show={titleChanged} value={original.title} />
        </section>

        <section className="flex flex-col gap-2">
          <span className="text-[13px] font-medium">Assign to</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Assign to"
                className={cn(
                  "flex h-[46px] w-full items-center justify-between gap-2.5 rounded-lg border border-border bg-card px-3 text-sm",
                  assigneeChanged && "border-tertiary",
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  {selectedPerson ? (
                    <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-tertiary text-[11px] font-semibold text-tertiary-foreground">
                      {personInitials(selectedPerson)}
                    </span>
                  ) : (
                    <User
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className={cn(
                      "truncate",
                      !selectedPerson && "text-muted-foreground",
                    )}
                  >
                    {selectedPerson
                      ? selectedPerson.id === currentUser?.id
                        ? `${personName(selectedPerson)} (me)`
                        : personName(selectedPerson)
                      : "Nobody yet"}
                  </span>
                </span>
                <ChevronDown
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-[var(--radix-dropdown-menu-trigger-width)]"
            >
              <DropdownMenuItem onSelect={() => setAssignedTo(null)}>
                <span className="flex-1">Nobody yet</span>
                {assignedTo === null && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
              {people.map((person) => (
                <DropdownMenuItem
                  key={person.id}
                  onSelect={() => setAssignedTo(person.id)}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tertiary text-[10px] font-semibold text-tertiary-foreground">
                    {personInitials(person)}
                  </span>
                  <span className="flex-1 truncate">
                    {person.id === currentUser?.id
                      ? `${personName(person)} (me)`
                      : personName(person)}
                  </span>
                  {assignedTo === person.id && <Check className="h-4 w-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <WasValue
            show={assigneeChanged}
            value={
              original.assignee ? personName(original.assignee) : "Unassigned"
            }
          />
        </section>

        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-medium">Due</span>
            {/* Clearing is a real edit shape — "15 Aug 2026 → No due date" —
                and the picker's own Clear lives a tap deeper, inside the
                calendar. This surfaces it where the field is. */}
            {dueDate && (
              <button
                type="button"
                // The visible word is "Clear"; the label says what of. It keeps
                // the visible text as a prefix, so voice control still matches
                // what is on screen.
                aria-label="Clear due date"
                onClick={() => setDueDate(null)}
                className="text-xs font-medium text-tertiary-ink"
              >
                Clear
              </button>
            )}
          </div>
          <DatePicker
            value={dueDate}
            onChange={setDueDate}
            aria-label="Due date"
            className={cn(dueDateChanged && "border-tertiary")}
          />
          {/* The same three offsets the raise sheet puts under its due date,
              setting the same real deadline — the one overdue badges, sorting
              and filtering all run off. */}
          <DateQuickPicks value={dueDate} onChange={setDueDate} />
          <WasValue
            show={dueDateChanged}
            value={formatDueDate(original.dueDate) ?? "No due date"}
          />
        </section>

        <section className="flex flex-col gap-2">
          <label
            htmlFor="issue-edit-description"
            className="text-[13px] font-medium"
          >
            Description
          </label>
          <Textarea
            id="issue-edit-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={3}
            className={cn(descriptionChanged && "border-tertiary")}
          />
          {/* The whole previous text, wrapping. A description is a sentence or
              two, and an amendment made without its own starting point in view
              is the overwrite this sheet exists to avoid. */}
          <WasValue
            show={descriptionChanged}
            value={original.description || "No description"}
          />
        </section>

        {errorMessage && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage}
          </p>
        )}
      </div>

      {/* Said plainly, before the tap. Every edit is stamped — no grace window,
          no silent save — and an edit that surprises someone afterwards is the
          failure mode this line exists to prevent. See [[issues.spec]]. */}
      <div className="flex items-center gap-2 px-4 pt-1 text-muted-foreground">
        <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="text-xs leading-4">
          Changes are recorded in this issue&apos;s activity.
        </span>
      </div>

      <div className="mt-3 flex gap-2 border-t border-border p-4">
        <Button
          variant="outline"
          className="basis-[38%]"
          onClick={onClose}
          disabled={editIssue.isPending}
        >
          Cancel
        </Button>
        <Button
          className="flex-1"
          onClick={submit}
          // Disabled rather than hidden: unlike an affordance someone may not
          // use, this one is theirs the moment they change something, and the
          // greyed button is what says so.
          disabled={!dirty || editIssue.isPending}
        >
          {editIssue.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </>
  );
}

/**
 * The value a changed field is moving away from, kept in sight beneath it.
 *
 * The point of the sheet is a considered amendment, not a silent overwrite —
 * and it is shown for a value being set for the first time too ("Was No due
 * date"), so the wording never has a hole in it where a null was.
 */
function WasValue({ show, value }: { show: boolean; value: string }) {
  if (!show) return null;
  return (
    // `items-start`, not centred: a previous description wraps to several
    // lines, and a vertically centred icon beside it reads as a bullet that
    // lost its list.
    <span className="flex items-start gap-1.5 text-xs text-muted-foreground [text-wrap:pretty]">
      <History className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
      <span>Was {value}</span>
    </span>
  );
}
